import { createApi } from '@reduxjs/toolkit/query/react';
import { baseQuery } from '../baseQuery';
import { User } from 'types/interfaces/user';

export const meApi = createApi({
  reducerPath: 'meApi',
  baseQuery: baseQuery,
  tagTypes: ['User'],
  endpoints: (builder) => ({
    getMe: builder.query<User, void>({
      query: () => ({
        url: '/users/me',
        method: 'GET',
        requiresAuth: true,
      }),
      providesTags: ['User'],
    }),
    updateAvatar: builder.mutation<User, { avatarUrl: string }>({
      query: ({ avatarUrl }) => ({
        url: '/users/avatar',
        method: 'PUT',
        body: JSON.stringify({ avatarUrl }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      invalidatesTags: ['User'],
    }),
    updateAnimatedBorder: builder.mutation<{ showAnimatedBorder: boolean }, { enabled: boolean }>({
      query: ({ enabled }) => ({
        url: '/settings/animated-border',
        method: 'PUT',
        body: JSON.stringify({ enabled }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      // Optimistic update: immediately reflect in cached user data
      async onQueryStarted({ enabled }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.showAnimatedBorder = enabled;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateBorderColors: builder.mutation<{ borderColor1: string; borderColor2: string }, { borderColor1: string; borderColor2: string }>({
      query: ({ borderColor1, borderColor2 }) => ({
        url: '/settings/border-colors',
        method: 'PUT',
        body: JSON.stringify({ borderColor1, borderColor2 }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      async onQueryStarted({ borderColor1, borderColor2 }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.borderColor1 = borderColor1;
            draft.borderColor2 = borderColor2;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateBubbleColors: builder.mutation<{ bubbleColor1: string; bubbleColor2: string }, { color1: string; color2: string }>({
      query: ({ color1, color2 }) => ({
        url: '/settings/bubble-colors',
        method: 'PUT',
        body: JSON.stringify({ color1, color2 }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      async onQueryStarted({ color1, color2 }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.bubbleColor1 = color1;
            draft.bubbleColor2 = color2;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateCustomSlug: builder.mutation<{ success: boolean; customSlug: string }, { slug: string }>({
      query: ({ slug }) => ({
        url: '/settings/custom-slug',
        method: 'PUT',
        body: JSON.stringify({ slug }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      async onQueryStarted({ slug }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.customSlug = slug;
            draft.customSlugSetAt = new Date().toISOString();
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    updateTradingPreferences: builder.mutation<
      { success: boolean; settings: { preferDomesticTrades: boolean } },
      { preferDomesticTrades: boolean }
    >({
      query: ({ preferDomesticTrades }) => ({
        url: '/settings/trading-preferences',
        method: 'PUT',
        body: JSON.stringify({ preferDomesticTrades }),
        headers: {
          'Content-Type': 'application/json',
        },
        requiresAuth: true,
      }),
      async onQueryStarted({ preferDomesticTrades }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.preferDomesticTrades = preferDomesticTrades;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    /**
     * Turn Vacation Mode on or off. `until` is the user's LOCAL calendar date
     * (YYYY-MM-DD) or null for no end date; the server stores 00:00Z of it and
     * rejects a date whose 00:00Z has already passed. The cached current user
     * is patched at once (the app-wide banner reads it) and rolled back on failure.
     */
    updateVacation: builder.mutation<
      { vacationMode: boolean; vacationUntil: string | null; vacationStartedAt: string | null },
      { enabled: boolean; until: string | null }
    >({
      query: (body) => ({
        url: '/settings/vacation',
        method: 'PUT',
        body,
        requiresAuth: true,
      }),
      async onQueryStarted({ enabled, until }, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.vacationMode = enabled;
            draft.vacationUntil = enabled && until ? `${until}T00:00:00.000Z` : null;
            if (!enabled) draft.vacationStartedAt = null;
          })
        );
        try {
          const { data } = await queryFulfilled;
          dispatch(
            meApi.util.updateQueryData('getMe', undefined, (draft) => {
              draft.vacationMode = data.vacationMode;
              draft.vacationUntil = data.vacationUntil;
              draft.vacationStartedAt = data.vacationStartedAt;
            })
          );
        } catch {
          patchResult.undo();
        }
      },
    }),
    markSmsAlertsIntroSeen: builder.mutation<
      { success: boolean; hasSeenSmsAlertsIntro: boolean },
      void
    >({
      query: () => ({
        url: '/users/me/has-seen-sms-alerts-intro',
        method: 'PATCH',
        requiresAuth: true,
      }),
      // Optimistically flip the cached flag so the intro modal never re-opens
      // in-session. Idempotent on the backend — safe for both confirm and dismiss.
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.hasSeenSmsAlertsIntro = true;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    markSmsAlertsLaunchAnnouncementSeen: builder.mutation<
      { success: boolean; hasSeenSmsAlertsLaunchAnnouncement: boolean },
      void
    >({
      query: () => ({
        url: '/users/me/has-seen-sms-alerts-launch-announcement',
        method: 'PATCH',
        requiresAuth: true,
      }),
      // Optimistically flip the cached flag so the announcement never re-fires
      // in-session even before the network round-trip completes. Idempotent on
      // the backend — safe for both primary CTA and dismiss.
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.hasSeenSmsAlertsLaunchAnnouncement = true;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
    removeCustomSlug: builder.mutation<{ success: boolean; customSlug: null }, void>({
      query: () => ({
        url: '/settings/custom-slug',
        method: 'DELETE',
        requiresAuth: true,
      }),
      async onQueryStarted(_, { dispatch, queryFulfilled }) {
        const patchResult = dispatch(
          meApi.util.updateQueryData('getMe', undefined, (draft) => {
            draft.customSlug = null;
            draft.customSlugSetAt = null;
          })
        );
        try {
          await queryFulfilled;
        } catch {
          patchResult.undo();
        }
      },
    }),
  }),
});

export const {
  useUpdateVacationMutation,
  useGetMeQuery,
  useUpdateAvatarMutation,
  useUpdateAnimatedBorderMutation,
  useUpdateBorderColorsMutation,
  useUpdateBubbleColorsMutation,
  useUpdateCustomSlugMutation,
  useRemoveCustomSlugMutation,
  useUpdateTradingPreferencesMutation,
  useMarkSmsAlertsIntroSeenMutation,
  useMarkSmsAlertsLaunchAnnouncementSeenMutation,
} = meApi;
